/**
 * RecycleBin — v109.9.53
 *
 * Consolidates soft-deleted resources across the app into one master-admin-
 * only page inside AdminHub. Sources:
 *   • Employees      — GET /admin/deleted-employees + POST /admin/restore-employee/:id
 *   • CRM clients    — GET /crm/clients/recycle-bin + POST /crm/clients/:id/restore
 *
 * Data is grouped in an inner Tabs component. Each row exposes a "Restore"
 * action and displays the same information in either List or Card view.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, RotateCcw, Users, Building2, AlertTriangle, Archive } from "lucide-react";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import ViewModeToggle from "../components/ViewModeToggle";
import { useDataView } from "../lib/useDataView";

function fmt(d) {
  if (!d) return "—";
  try { return new Date(d).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return String(d); }
}

export default function RecycleBin() {
  const { user } = useAuth();
  const isMaster = user?.role === "master_admin";
  const [tab, setTab] = useState("universal");
  const [employees, setEmployees] = useState([]);
  const [clients, setClients] = useState([]);
  const [universal, setUniversal] = useState([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useDataView("recycle_bin", "list");

  const load = async () => {
    setLoading(true);
    try {
      const [eRes, cRes, uRes] = await Promise.all([
        api.get("/employees", { params: { only_deleted: true, include_temp: true } }).catch(() => ({ data: [] })),
        api.get("/crm/clients/recycle-bin").catch(() => ({ data: [] })),
        api.get("/admin/recycle-bin").catch(() => ({ data: [] })),
      ]);
      setEmployees(Array.isArray(eRes.data) ? eRes.data : []);
      setClients(Array.isArray(cRes.data) ? cRes.data : (cRes.data?.items || []));
      setUniversal(Array.isArray(uRes.data) ? uRes.data : []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const restoreEmployee = async (id) => {
    if (!isMaster) return toast.error("Only Master Admin can restore items from the Recycle Bin");
    if (!window.confirm("Restore this employee back into the roster?")) return;
    try {
      await api.post(`/employees/${id}/restore`);
      toast.success("Employee restored");
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const restoreClient = async (id) => {
    if (!isMaster) return toast.error("Only Master Admin can restore items from the Recycle Bin");
    if (!window.confirm("Restore this client back into the CRM?")) return;
    try {
      await api.post(`/crm/clients/${id}/restore`);
      toast.success("Client restored");
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const restoreUniversal = async (r) => {
    if (!isMaster) return toast.error("Only Master Admin can restore items from the Recycle Bin");
    if (!window.confirm(`Restore "${r.label}" back into ${r.module_label}?`)) return;
    try {
      await api.post(`/admin/recycle-bin/${r.id}/restore`);
      toast.success(`${r.module_label} restored`);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const purgeUniversal = async (r) => {
    if (!isMaster) return toast.error("Only Master Admin can permanently delete items from the Recycle Bin");
    if (!window.confirm(`Permanently delete "${r.label}"? This CANNOT be undone.`)) return;
    try {
      await api.delete(`/admin/recycle-bin/${r.id}`);
      toast.success("Permanently deleted");
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const emptyBin = async () => {
    if (!isMaster) return toast.error("Only Master Admin can empty the Recycle Bin");
    let total = 0;
    if (tab === "employees") total = employees.length;
    else if (tab === "clients") total = clients.length;
    else total = universal.length;
    if (total === 0) return toast.info("Nothing to empty");
    if (!window.confirm(`Permanently delete all ${total} soft-deleted ${tab}? This CANNOT be undone.`)) return;
    if (!window.confirm(`Are you absolutely sure? Type-safe: this will hard-delete ${total} record(s).`)) return;
    try {
      if (tab === "universal") {
        const { data } = await api.delete("/admin/recycle-bin");
        toast.success(`Emptied ${data?.purged ?? total} record(s) permanently`);
      } else {
        const path = tab === "employees" ? "/admin/recycle-bin/empty-employees" : "/admin/recycle-bin/empty-clients";
        const { data } = await api.post(path);
        toast.success(`Emptied ${data?.deleted ?? total} record(s) permanently`);
      }
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed max-w-2xl">
          All soft-deleted records across the platform. Anyone with <b>Delete</b> permission on a module can move a record here — but only <b>Master Admin</b> can restore or permanently empty this bin.
          {!isMaster && (
            <span className="block mt-1 text-amber-600 dark:text-amber-400 font-semibold flex items-center gap-1">
              <AlertTriangle size={12} /> You are in read-only mode. Restore &amp; Empty are disabled.
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isMaster && (
            <Button
              onClick={emptyBin}
              variant="outline"
              className="h-8 text-[11px] text-rose-600 border-rose-300 hover:bg-rose-50 dark:text-rose-300 dark:border-rose-500/50 dark:hover:bg-rose-500/15"
              data-testid="recycle-empty-bin"
            >
              <Trash2 size={12} className="mr-1" /> Empty This Tab
            </Button>
          )}
          <ViewModeToggle mode={view} onChange={setView} testid="recycle-view-toggle" />
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="recycle-tabs">
          <TabsTrigger value="universal" data-testid="recycle-tab-universal">
            <Archive size={13} className="mr-1.5" /> Everything ({universal.length})
          </TabsTrigger>
          <TabsTrigger value="employees" data-testid="recycle-tab-employees">
            <Users size={13} className="mr-1.5" /> Employees ({employees.length})
          </TabsTrigger>
          <TabsTrigger value="clients" data-testid="recycle-tab-clients">
            <Building2 size={13} className="mr-1.5" /> CRM Clients ({clients.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="universal" className="mt-3">
          <RecycleList
            rows={universal}
            view={view}
            emptyLabel="Nothing has been soft-deleted from the platform yet"
            columns={[
              { key: "label", label: "Item" },
              { key: "module_label", label: "Module" },
              { key: "deleted_by_name", label: "Deleted By" },
              { key: "deleted_reason", label: "Reason" },
              { key: "deleted_at", label: "Deleted", fmt: fmt },
            ]}
            title={(r) => r.label}
            subtitle={(r) => `${r.module_label} · by ${r.deleted_by_name || "—"}`}
            meta={(r) => [r.module_label, r.deleted_by_name, fmt(r.deleted_at), r.deleted_reason || null].filter(Boolean)}
            onRestore={restoreUniversal}
            onPurge={purgeUniversal}
            testid="recycle-universal"
          />
        </TabsContent>

        <TabsContent value="employees" className="mt-3">
          <RecycleList
            rows={employees}
            view={view}
            emptyLabel="No soft-deleted employees"
            columns={[
              { key: "name", label: "Name" },
              { key: "email", label: "Email" },
              { key: "role", label: "Role" },
              { key: "deleted_at", label: "Deleted", fmt: fmt },
            ]}
            title={(r) => r.name}
            subtitle={(r) => r.email}
            meta={(r) => [r.role, fmt(r.deleted_at)]}
            onRestore={(r) => restoreEmployee(r.id)}
            testid="recycle-employees"
          />
        </TabsContent>

        <TabsContent value="clients" className="mt-3">
          <RecycleList
            rows={clients}
            view={view}
            emptyLabel="No soft-deleted clients"
            columns={[
              { key: "client_name", label: "Client" },
              { key: "primary_contact", label: "Contact" },
              { key: "state", label: "State" },
              { key: "deleted_at", label: "Deleted", fmt: fmt },
            ]}
            title={(r) => r.client_name}
            subtitle={(r) => r.primary_contact || r.email || "—"}
            meta={(r) => [r.state, fmt(r.deleted_at)]}
            onRestore={(r) => restoreClient(r.id)}
            testid="recycle-clients"
          />
        </TabsContent>
      </Tabs>
      {loading && <div className="text-center text-[11px] text-slate-500 py-2">Loading…</div>}
    </div>
  );
}

function RecycleList({ rows, view, emptyLabel, columns, title, subtitle, meta, onRestore, onPurge, testid }) {
  if (!rows?.length) {
    return (
      <div className="pk-card p-10 text-center" data-testid={`${testid}-empty`}>
        <Trash2 size={28} className="mx-auto mb-2 text-slate-300 dark:text-slate-600" />
        <div className="text-[12px] text-slate-500 dark:text-slate-400">{emptyLabel}</div>
      </div>
    );
  }
  if (view === "list") {
    return (
      <div className="pk-card overflow-x-auto" data-testid={`${testid}-list`}>
        <table className="w-full text-[12px]">
          <thead>
            <tr className="border-b border-slate-200 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-800/50">
              {columns.map((c) => (
                <th key={c.key} className="text-left px-3 py-2 font-bold text-[11px] uppercase tracking-wider text-slate-600 dark:text-slate-300">{c.label}</th>
              ))}
              <th className="text-right px-3 py-2 font-bold text-[11px] uppercase tracking-wider text-slate-600 dark:text-slate-300">Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-slate-100 dark:border-slate-800 hover:bg-indigo-50/30 dark:hover:bg-indigo-500/8" data-testid={`${testid}-row-${r.id}`}>
                {columns.map((c) => (
                  <td key={c.key} className="px-3 py-2 text-slate-800 dark:text-slate-100">
                    {c.fmt ? c.fmt(r[c.key]) : (r[c.key] ?? "—")}
                  </td>
                ))}
                <td className="px-3 py-2 text-right">
                  <div className="inline-flex items-center gap-1 justify-end">
                    <Button size="sm" variant="outline" onClick={() => onRestore(r)} className="h-7 text-[11px]" data-testid={`${testid}-restore-${r.id}`}>
                      <RotateCcw size={12} className="mr-1" /> Restore
                    </Button>
                    {onPurge && (
                      <Button size="sm" variant="outline" onClick={() => onPurge(r)} className="h-7 text-[11px] text-rose-600 dark:text-rose-300 border-rose-300 dark:border-rose-500/50 hover:bg-rose-50 dark:hover:bg-rose-500/15" data-testid={`${testid}-purge-${r.id}`}>
                        <Trash2 size={12} className="mr-1" /> Delete
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }
  // Card view — SAME info as list view
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3" data-testid={`${testid}-cards`}>
      {rows.map((r) => (
        <div key={r.id} className="pk-card p-3" data-testid={`${testid}-card-${r.id}`}>
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="min-w-0">
              <div className="text-[13.5px] font-bold text-slate-900 dark:text-white truncate">{title(r)}</div>
              <div className="text-[11px] text-slate-500 dark:text-slate-400 truncate">{subtitle(r)}</div>
            </div>
            <Button size="sm" variant="outline" onClick={() => onRestore(r)} className="h-7 text-[11px] shrink-0" data-testid={`${testid}-restore-card-${r.id}`}>
              <RotateCcw size={12} className="mr-1" /> Restore
            </Button>
            {onPurge && (
              <Button size="sm" variant="outline" onClick={() => onPurge(r)} className="h-7 text-[11px] shrink-0 text-rose-600 dark:text-rose-300 border-rose-300 dark:border-rose-500/50 hover:bg-rose-50 dark:hover:bg-rose-500/15" data-testid={`${testid}-purge-card-${r.id}`}>
                <Trash2 size={12} className="mr-1" /> Delete
              </Button>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            {meta(r).map((m, i) => (
              <Badge key={i} className="bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-700 text-[10px]">{m || "—"}</Badge>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
